// @ts-nocheck

import { map, sortBy, values } from 'lodash';

import { type StateType } from '../../../states';
import type { BetslipDisplayStatus } from '../../../types/betslip';
import compose from '../../../utils/compose';
import { HISTORY_PAGE_SIZE } from '../constants';
import Grid from '../states/grid';
import Pool from '../states/pool';
import { GridTypes } from '../types';
import type { SystemGridWithAmount, Results, GridMatchLength, GridState, GridBetslip } from '../types';

import { getSystemGridWithAmount } from './common';
import { getGrid, getGrids } from './grids';
import { getPool, getPoolReports } from './pool';
import { getGridContributors } from './public';

const getHistoryState = (state: StateType) => state.grids.history;

export const getCurrentGrids = (state: StateType) => {
  const { gridReferences } = getHistoryState(state);
  const grids = getGrids(state);
  // $FlowFixMe
  return gridReferences?.map((reference) => grids[reference]).filter((k) => k) || [];
};

export const getFilter = (state: StateType) => {
  const { filter } = getHistoryState(state);
  return filter;
};

export const getCurrentPageOffset = (state: StateType) => {
  const { currentPage } = getHistoryState(state);
  return currentPage * HISTORY_PAGE_SIZE;
};

export const getGridCount = (state: StateType) => {
  const { gridCount } = getHistoryState(state);
  return gridCount;
};

export const getIsFetching = (state: StateType) => {
  const { isFetching } = getHistoryState(state);
  return isFetching;
};

export const getCurrentPage = (state: StateType) => {
  const { currentPage } = getHistoryState(state);
  return currentPage;
};

export const getSortedGrids = (state: StateType) => {
  const gridCount = getGridCount(state);
  const grids = getCurrentGrids(state);
  const userNickname = state.user.nickname;
  if (!gridCount || !Array.isArray(grids)) {
    return [];
  }
  return compose(
    (grids) => map(grids, (k) => k),
    (grids) =>
      sortBy(grids, (grid) => {
        const userFirstBet = getUserFirstBet(grid, userNickname);
        return userFirstBet ? -userFirstBet.validationDate : -1;
      }),
  )(grids);
};

export const getGridsPerPool = (state: StateType) => {
  const sortedGrids = getSortedGrids(state);
  return sortedGrids.reduce((acc, cur) => {
    const { poolId, gridReference } = cur;
    if (acc[poolId]) {
      acc[poolId].push(gridReference);
    } else {
      acc[poolId] = [gridReference];
    }
    return acc;
  }, {});
};

type EnhancedGrid = {
  poolNumber: number;
  gridLength: GridMatchLength | null;
  betslipStatus: BetslipDisplayStatus | null;
  statusLabel: string;
  date: number | null;
  winningsAmount: number | null;
  gridReference: string;
  nbGridsOnPool: number;
  indexGridsOnPool: number;
};

const getEnhancedGrids = (state, filteredGrids) => {
  const gridsPerPools = getGridsPerPool(state);
  const userNickname = state.user.nickname;

  return filteredGrids.map<EnhancedGrid>((grid) => {
    const { poolId, gridReference, allWinningsAmount } = grid;
    const pool = getPool(state, poolId);
    const betslipStatus = Grid.getBetslipStatus(grid);
    const statusLabel = Grid.getBetslipStatusLabel(grid);
    const winningsAmount = allWinningsAmount === 0 ? null : getUserWinningsAmount(grid, userNickname);
    const gridRefsFromPool = [...gridsPerPools[poolId]].reverse();
    const nbGridsOnPool = gridRefsFromPool.length;
    const indexGridsOnPool = gridRefsFromPool.indexOf(gridReference) + 1;

    return {
      poolNumber: Pool.getNumber(poolId),
      gridLength: Pool.getType(poolId),
      betslipStatus,
      statusLabel,
      date: pool?.poolEnd,
      winningsAmount,
      gridReference,
      nbGridsOnPool,
      indexGridsOnPool,
    };
  });
};

export const getFilteredGrids = (state: StateType) => {
  const sortedGrids = getSortedGrids(state);
  return getEnhancedGrids(state, sortedGrids);
};

export const getGridsList = (state: StateType) => {
  const filter = getFilter(state);
  return {
    grids: getFilteredGrids(state),
    currentFilter: filter,
  };
};

export const getHistoryResults = (state: StateType, { gridReference }: { gridReference: string }) => {
  let results = [];

  const grid = getGrid(state, gridReference);
  const pool = getPool(state, grid?.poolId);
  let isLive = false;
  let noResults = false;

  if (grid && pool) {
    isLive = Pool.isLive(pool);
    const payouts = Pool.getPoolPayouts(pool);
    let nbMatchesForPayout = 0;
    if (payouts && payouts.length > 0) {
      nbMatchesForPayout = payouts[payouts.length - 1].nbCorrectResults;
    }

    results = Grid.getResults(grid, pool).map<Results>((result, index) => ({
      label: index,
      value: result,
      isHighlight: result > 0 && index > nbMatchesForPayout,
    }));
    results.reverse();

    const isFunding = Grid.isFunding(grid);
    const isCancelled = Grid.isCancelled(grid);
    noResults = isFunding || isCancelled;
  }

  return {
    results,
    isLive,
    noResults,
  };
};

export const getHistoryRepartitions = (state: StateType, { gridReference }: { gridReference: string }) => {
  let repartitions = [];
  const grid = getGrid(state, gridReference);

  if (grid) {
    const { betslips, author } = grid;
    const { nickname: authorNickname } = author;
    const uniqContributors = betslips.reduce((acc, cur) => {
      const { nickname, betAmount, avatarPath, winningsAmount } = cur;
      if (acc[nickname]) {
        acc[nickname].betAmount += betAmount || 0;
        acc[nickname].winningsAmount += winningsAmount || 0;
      } else {
        acc[nickname] = {
          betAmount,
          nickname,
          avatarPath,
          isAuthor: authorNickname === nickname,
          winningsAmount,
        };
      }
      return acc;
    }, {});
    repartitions = values(uniqContributors);
  }

  return {
    repartitions,
    allBetAmount: grid?.allBetAmount,
    allWinningsAmount: grid?.allWinningsAmount,
  };
};

export const getHistoryReports = (state: StateType, { gridReference }: { gridReference: string }) => {
  const grid = getGrid(state, gridReference);
  if (grid) {
    return getPoolReports(state, { poolId: grid.poolId });
  }

  return {
    payouts: [],
  };
};

const getUserWinningsAmount = (grid: GridState, nickname: string | null): number => {
  if (grid?.betslips) {
    return grid.betslips.reduce(
      (acc, betslip) => (betslip.nickname === nickname ? acc + (betslip.winningsAmount || 0) : acc),
      0,
    );
  }
  return 0;
};

const getUserBetAmount = (grid: GridState, nickname: string | null): number => {
  if (grid?.betslips) {
    return grid.betslips.reduce(
      (acc, betslip) => (betslip.nickname === nickname ? acc + (betslip.betAmount || 0) : acc),
      0,
    );
  }
  return 0;
};

const getUserGridBets = (grid: GridState, nickname: string | null): Array<GridBetslip> => {
  if (grid?.betslips) {
    return grid.betslips.filter((betslip) => betslip.nickname === nickname);
  }
  return [];
};

const getUserFirstBet = (grid: GridState, nickname: string | null): GridBetslip | null => {
  const userBets = getUserGridBets(grid, nickname);
  if (userBets.length) {
    return userBets.sort((a, b) => (a.validationDate - b.validationDate ? -1 : 1))[0];
  }
  return null;
};

export const getHistoryHeader = (
  state: StateType,
  { gridReference, isShare = false }: { gridReference: string; isShare?: boolean },
) => {
  const grid = (gridReference && getGrid(state, gridReference)) || null;
  const userNickname = state.user.nickname;
  const isTeamGrid = grid?.gridType === GridTypes.TEAM;
  let betAmount = 0;
  let winningsAmount = 0;
  let status = '';
  let statusLabel = '';

  if (grid) {
    betAmount = isTeamGrid && !isShare ? getUserBetAmount(grid, userNickname) : grid.allBetAmount;
    winningsAmount = isTeamGrid && !isShare ? getUserWinningsAmount(grid, userNickname) : grid.allWinningsAmount;
    status = Grid.getBetslipStatus(grid);
    statusLabel = Grid.getBetslipStatusLabel(grid);
  }

  return {
    betAmount,
    winningsAmount,
    status,
    statusLabel,
    gridAmount: Grid.getAmount(grid),
  };
};

export const historyGridView = (
  state: StateType,
  { gridReference, isPublicGrid = false }: { gridReference: string; isPublicGrid: boolean },
) => {
  let title = '';
  let contributors = [];
  const poolId = null;
  let isFunding = false;
  let isCancelled = false;
  let isSystem = false;
  let finalIsPublicGrid = isPublicGrid;

  const grid = getGrid(state, gridReference);
  if (grid) {
    const { poolId, isSystem: isGridSystem, gridType } = grid;
    const pool = getPool(state, poolId);
    title = pool?.poolTitle;
    isFunding = Grid.isFunding(grid);
    isCancelled = Grid.isCancelled(grid);
    finalIsPublicGrid = GridTypes.TEAM === gridType;

    contributors = isFunding ? getGridContributors(grid) : [];
    isSystem = isGridSystem;
  }

  return {
    title,
    poolId,
    contributors,
    isPublicGrid: finalIsPublicGrid,
    isFunding,
    isCancelled,
    isSystem,
  };
};

export const historyDetailsSystemSelector = (state: StateType, { gridReference }: { gridReference: string }) => {
  let selections = null;
  let matches = [];
  let result = null;

  const grid = gridReference && getGrid(state, gridReference);
  if (grid) {
    const { poolId, systemStrGridSelections } = grid;
    const pool = getPool(state, poolId);
    matches = pool?.matches;
    selections = systemStrGridSelections?.map<SystemGridWithAmount>(getSystemGridWithAmount);
    result = pool?.strPoolResult;
  }

  return {
    matches,
    selections,
    result,
  };
};

export const getHistoryDetailRoute = (state: StateType, props: { params: { gridReference: string | null } }) => {
  const gridReference = props?.params?.gridReference || null;
  const { connected } = state.user;
  const isFetching = getIsFetching(state);

  const grid = getGrid(state, gridReference);
  const shouldFetch = connected && grid === undefined;
  const hasNoGrid = grid === null;

  return {
    gridReference,
    connected,
    shouldFetch,
    hasNoGrid,
    isFetching,
  };
};

export const getShareDetailRoute = (state: StateType, props: { params: { gridReference: string | null } }) => {
  const gridReference = props.params?.gridReference || null;
  const isFetching = getIsFetching(state);

  const grid = getGrid(state, gridReference);
  const shouldFetch = grid === undefined;
  const hasNoGrid = grid === null;

  return {
    gridReference,
    shouldFetch,
    hasNoGrid,
    isFetching,
  };
};
